import { useConfigurations } from "@via/api/configuration";
import { codesReminderPath } from "@via/api/mfa-destination";
import { useAuthStatusDetails } from "@via/pages/authentication/mfa/mfa.api.tsx";
import { BackendRedirect } from "@via/utils/backend-redirect";
import { ROUTES } from "@via/utils/constants.tsx";
import {
  consumePostLoginRedirect,
  isBackendRoute,
} from "@via/utils/post-login-redirect";
import { Fragment, type JSX, useState } from "react";
import { Navigate } from "react-router-dom";

/**
 * The signed in landing at "/". An OAuth or SAML sign-in ends here with a redirect, never
 * through the login response, so this is where it learns from the status call that the
 * recovery codes were never confirmed as saved, and shows that reminder first.
 *
 * With MFA off (or no configuration to read) there is no reminder to show, so no status call
 * is made and the landing goes straight to its destination.
 */
export default function HomeRedirect(): JSX.Element {
  // Read once: the stored redirect is removed as it is read, and the status call re-renders.
  const [stored] = useState(consumePostLoginRedirect);
  const configurations = useConfigurations();
  const mfaOn = (configurations.data?.MFA_ENFORCEMENT ?? "off") !== "off";
  const status = useAuthStatusDetails(mfaOn);

  if (configurations.isPending || (mfaOn && status.isPending)) {
    return <Fragment />;
  }
  const destination = stored ?? ROUTES.ACTIVITY;
  if (mfaOn && status.data?.mfaCodesPending) {
    return <Navigate to={codesReminderPath(destination)} replace />;
  }
  // A /api/* target (the OAuth consent resume after an SSO round trip) is served by the
  // backend, not the SPA router, so it needs a full page load.
  if (isBackendRoute(destination)) return <BackendRedirect to={destination} />;
  return <Navigate to={destination} replace />;
}
